// Phase 9 /admin/raw-presets — admin-only raw YAML inspector.
//
// Phase 8 R1 stripped raw YAML / parsed dicts / file paths from the
// operator-facing ``/api/presets`` listing. For the rare case where
// an admin needs to inspect the YAML body (e.g. diagnosing why a
// scenario compile resolved to a given coefficient) they hit the
// existing ``/api/presets/raw?id=<id>`` surface from this page.
//
// Monaco is out of scope for Phase 9 per the brief — we render the
// YAML in a simple <pre> block with a code-style treatment.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { presets as presetsApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

export default function AdminRawPresetsPage() {
    const [cards, setCards] = useState([]);
    const [selected, setSelected] = useState("");
    const [yaml, setYaml] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const data = await presetsApi.list();
                const arr = Array.isArray(data) ? data : data?.items || [];
                // Only system presets — user presets have their own surface.
                setCards(arr.filter((c) => !c.is_user_preset));
            } catch (e) {
                setErr(formatApiError(e, "Failed to load presets"));
            }
        })();
    }, []);

    useEffect(() => {
        if (!selected) return;
        setLoading(true);
        (async () => {
            try {
                const raw = await presetsApi.raw(selected);
                setYaml(typeof raw === "string" ? raw : raw?.yaml || JSON.stringify(raw, null, 2));
                setErr("");
            } catch (e) {
                setErr(formatApiError(e, "Failed to load YAML"));
                setYaml("");
            } finally {
                setLoading(false);
            }
        })();
    }, [selected]);

    const grouped = useMemo(() => {
        const out = {};
        for (const c of cards) {
            const fam = c.family || "other";
            out[fam] = out[fam] || [];
            out[fam].push(c);
        }
        return out;
    }, [cards]);

    return (
        <div data-testid="admin-raw-presets-page">
            <PageHeader
                title="Raw Preset Access"
                subtitle="Inspect the YAML body of a system preset. Admin-only — hidden from operators."
            />
            {err && (
                <div className="mb-4 p-3 rounded-md border border-ux-danger/40 bg-ux-danger/10 text-ux-danger text-[13px]">
                    {err}
                </div>
            )}
            <div className="grid gap-4 md:grid-cols-[280px_1fr]">
                <aside
                    className="rounded-lg border border-ux-border-1 bg-ux-bg-1 p-2 max-h-[70vh] overflow-y-auto"
                    data-testid="admin-raw-presets-list"
                >
                    {Object.entries(grouped).map(([fam, items]) => (
                        <div key={fam} className="mb-3">
                            <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-ux-text-3">
                                {fam}
                            </div>
                            {items.map((c) => {
                                const id = c.id;
                                const active = selected === id;
                                return (
                                    <button
                                        key={id}
                                        onClick={() => setSelected(id)}
                                        className={[
                                            "w-full text-left px-2 py-1.5 rounded-md text-[12px] transition-colors",
                                            active
                                                ? "bg-ux-accent/15 text-ux-text-1"
                                                : "text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2",
                                        ].join(" ")}
                                        data-testid={`admin-raw-presets-item-${id}`}
                                    >
                                        {c.name || id}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                    {cards.length === 0 && (
                        <div className="px-2 py-3 text-[12px] text-ux-text-3">No presets.</div>
                    )}
                </aside>
                <main
                    className="rounded-lg border border-ux-border-1 bg-ux-bg-1 overflow-hidden"
                    data-testid="admin-raw-presets-body"
                >
                    <div className="px-4 py-2 border-b border-ux-border-1 text-[12px] font-mono text-ux-text-3">
                        {selected ? selected : "Select a preset to view its YAML…"}
                    </div>
                    <pre
                        className="p-4 text-[12px] font-mono text-ux-text-2 bg-ux-bg-0/40 overflow-auto max-h-[68vh] whitespace-pre"
                        data-testid="admin-raw-presets-yaml"
                    >
                        {loading ? "loading…" : yaml || "(nothing selected)"}
                    </pre>
                </main>
            </div>
        </div>
    );
}
